/**
 * OneSignal Push Notifications Service
 * Using NativelyNotifications JavaScript SDK
 * 
 * Documentation: https://natively.app/documentation/nativelynotifications
 * 
 * IMPORTANT: This service only works in native iOS/Android apps, not in web browsers.
 */

import { isNativeApp } from "./platform";

// TypeScript declarations for NativelyNotifications
declare global {
  interface Window {
    NativelyNotifications: any;
  }
}

interface OneSignalResponse {
  playerId?: string;
  status?: boolean;
  error?: string;
  message?: string;
  externalId?: string;
}

interface ExternalIdSetResponse {
  externalId?: string;
  error?: string;
  message?: string;
}

export class OneSignalService {
  private notifications: any;
  private initialized: boolean = false;

  constructor() {
    this.notifications = null;
  }

  /**
   * Initialize the OneSignal notifications instance
   * This will wait for the SDK to load if it's not available yet
   */
  async initialize(): Promise<boolean> {
    if (this.initialized) {
      console.log('[OneSignal] Already initialized');
      return true;
    }
    
    // Check if running in native app
    if (!isNativeApp()) {
      console.log('[OneSignal] Skipping initialization - not running in native app');
      return false;
    }
    
    // If SDK is already available, initialize immediately
    if (typeof window !== 'undefined' && window.NativelyNotifications) {
      try {
        this.notifications = new window.NativelyNotifications();
        this.initialized = true;
        console.log('[OneSignal] Initialized successfully');
        return true;
      } catch (error) {
        console.error('[OneSignal] Failed to create NativelyNotifications instance:', error);
        this.notifications = null;
        return false;
      }
    }
    
    // SDK not available yet - wait for it to load (up to 5 seconds)
    console.log('[OneSignal] Waiting for NativelyNotifications SDK to load...');
    const maxWaitMs = 5000;
    const checkIntervalMs = 100;
    const startTime = Date.now();
    
    while (Date.now() - startTime < maxWaitMs) {
      if (typeof window !== 'undefined' && window.NativelyNotifications) {
        try {
          this.notifications = new window.NativelyNotifications();
          this.initialized = true;
          console.log('[OneSignal] SDK loaded and initialized');
          return true;
        } catch (error) {
          console.error('[OneSignal] Failed to create NativelyNotifications instance:', error);
          this.notifications = null;
          return false;
        }
      }
      
      // Wait before checking again
      await new Promise(resolve => setTimeout(resolve, checkIntervalMs));
    }
    
    console.error('[OneSignal] Timeout waiting for NativelyNotifications SDK');
    return false;
  }

  /**
   * Check if OneSignal is available
   */
  isAvailable(): boolean {
    return this.initialized && this.notifications !== null;
  }

  /**
   * Request push notification permission
   * @param fallbackToSettings - Show alert if permission is denied
   * @returns Promise with permission status
   */
  async requestPermission(fallbackToSettings: boolean = false): Promise<boolean> {
    if (!this.isAvailable()) {
      console.error('[OneSignal] Cannot request permission - service not available');
      throw new Error('OneSignal not initialized');
    }

    if (!this.notifications) {
      console.error('[OneSignal] Cannot request permission - notifications object is null');
      throw new Error('OneSignal notifications object not available');
    }

    // Verify requestPermission method exists
    if (typeof this.notifications.requestPermission !== 'function') {
      console.error('[OneSignal] requestPermission method not found on notifications object');
      console.error('[OneSignal] Available methods:', Object.keys(this.notifications));
      throw new Error('OneSignal requestPermission method not available');
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Timeout waiting for permission request response'));
      }, 30000); // 30 second timeout for permission prompt

      const callback = (resp: OneSignalResponse) => {
        clearTimeout(timeoutId);
        console.log('[OneSignal] Permission response:', resp);
        resolve(resp.status || false);
      };

      try {
        console.log('[OneSignal] Requesting permission with fallbackToSettings:', fallbackToSettings);
        this.notifications.requestPermission(fallbackToSettings, callback);
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('[OneSignal] Error calling requestPermission:', error);
        reject(error);
      }
    });
  }

  /**
   * Get the OneSignal Player ID (unique device identifier)
   * @returns Promise with player ID
   */
  async getOneSignalId(): Promise<string | null> {
    if (!this.isAvailable()) {
      console.error('[OneSignal] Cannot get player ID - service not available');
      throw new Error('OneSignal not initialized');
    }

    if (!this.notifications) {
      console.error('[OneSignal] Cannot get player ID - notifications object is null');
      throw new Error('OneSignal notifications object not available');
    }

    // Verify getOneSignalId method exists
    if (typeof this.notifications.getOneSignalId !== 'function') {
      console.error('[OneSignal] getOneSignalId method not found on notifications object');
      console.error('[OneSignal] Available methods:', Object.keys(this.notifications));
      throw new Error('OneSignal getOneSignalId method not available');
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Timeout waiting for player ID response'));
      }, 10000); // 10 second timeout

      const callback = (resp: OneSignalResponse) => {
        clearTimeout(timeoutId);
        
        if (resp.playerId) {
          console.log('[OneSignal] Player ID:', resp.playerId);
          resolve(resp.playerId);
        } else {
          console.error('[OneSignal] Failed to get player ID, response:', resp);
          resolve(null);
        }
      };

      try {
        console.log('[OneSignal] Requesting player ID...');
        this.notifications.getOneSignalId(callback);
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('[OneSignal] Error calling getOneSignalId:', error);
        reject(error);
      }
    });
  }

  /**
   * Get current permission status
   * @returns Promise with permission status
   */
  async getPermissionStatus(): Promise<boolean> {
    if (!this.isAvailable()) {
      throw new Error('OneSignal not initialized');
    }

    return new Promise((resolve) => {
      const callback = (resp: OneSignalResponse) => {
        console.log('[OneSignal] Current permission status:', resp.status);
        resolve(resp.status || false);
      };

      this.notifications.getPermissionStatus(callback);
    });
  }

  /**
   * Set external user ID (e.g., your database user ID)
   * This links the OneSignal player to your user
   * @param externalId - Your user's unique ID
   * @returns Promise with result
   */
  async setExternalId(externalId: string): Promise<boolean> {
    if (!this.isAvailable()) {
      console.error('[OneSignal] Cannot set external ID - service not available');
      throw new Error('OneSignal not initialized');
    }

    if (!this.notifications) {
      console.error('[OneSignal] Cannot set external ID - notifications object is null');
      throw new Error('OneSignal notifications object not available');
    }

    // Verify setExternalId method exists
    if (typeof this.notifications.setExternalId !== 'function') {
      console.error('[OneSignal] setExternalId method not found on notifications object');
      console.error('[OneSignal] Available methods:', Object.keys(this.notifications));
      throw new Error('OneSignal setExternalId method not available');
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Timeout waiting for setExternalId response'));
      }, 10000); // 10 second timeout

      const callback = (resp: ExternalIdSetResponse) => {
        clearTimeout(timeoutId);
        
        if (resp && resp.externalId) {
          console.log('[OneSignal] External ID set successfully:', resp.externalId);
          resolve(true);
        } else {
          const errorMessage = (resp && resp.error) || (resp && resp.message) || "Failed to set external ID";
          console.error('[OneSignal]', errorMessage);
          resolve(false);
        }
      };

      try {
        console.log('[OneSignal] Calling setExternalId with:', { externalId });
        this.notifications.setExternalId({ externalId }, callback);
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('[OneSignal] Error calling setExternalId:', error);
        reject(error);
      }
    });
  }

  /**
   * Get current external user ID
   * @returns Promise with external ID or null
   */
  async getExternalId(): Promise<string | null> {
    if (!this.isAvailable()) {
      throw new Error('OneSignal not initialized');
    }

    return new Promise((resolve) => {
      const callback = (resp: any) => {
        // Check if the response is a valid array with at least one object
        const res = (Array.isArray(resp) && resp.length > 0) ? resp[0] : null;
        
        if (res && res.externalId) {
          console.log('[OneSignal] Current external ID:', res.externalId);
          resolve(res.externalId);
        } else {
          const errorMessage = (res && res.error) || (res && res.message) || "Failed to get external ID";
          console.error('[OneSignal]', errorMessage);
          resolve(null);
        }
      };

      this.notifications.getExternalId(callback);
    });
  }

  /**
   * Remove external user ID
   * @returns Promise with success status
   */
  async removeExternalId(): Promise<boolean> {
    if (!this.isAvailable()) {
      throw new Error('OneSignal not initialized');
    }

    return new Promise((resolve) => {
      const callback = (resp: any) => {
        if (resp && (resp.error || resp.message)) {
          const errorMessage = resp.error || resp.message;
          console.error('[OneSignal] Failed to remove external ID:', errorMessage);
          resolve(false);
        } else {
          console.log('[OneSignal] External ID removed successfully');
          resolve(true);
        }
      };

      this.notifications.removeExternalId(callback);
    });
  }
}

// Export singleton instance
export const oneSignalService = new OneSignalService();
